import type { ReactElement } from "react";
import type { ControlPlanParams } from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import { cn } from "../../lib/utils.js";

export function ControlPlanWidget({
  data,
}: WidgetBodyProps<ControlPlanParams>): ReactElement {
  return <ControlList data={data} />;
}

export function ControlList({
  data,
  selectedId,
  onSelect,
  locked = false,
}: {
  data: ControlPlanParams;
  selectedId?: string | undefined;
  onSelect?: ((id: string) => void) | undefined;
  locked?: boolean;
}): ReactElement {
  if (!data.rows.length)
    return <p className="p-4 text-sm text-pb-text-muted">No controls yet.</p>;
  return (
    <div
      role="region"
      aria-label="Monitoring control list"
      // Keyboard access for the independently scrolling canvas list.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      className="@container min-h-0 min-w-0 flex-1 overflow-auto"
    >
      <ul className="divide-y divide-pb-border">
        {data.rows.map((row) => (
          <li key={row.id}>
            {onSelect ? (
              <button
                type="button"
                disabled={locked}
                aria-pressed={selectedId === row.id}
                onClick={() => onSelect(row.id)}
                className={cn(
                  "w-full rounded-md border border-transparent p-4 text-start hover:pb-overlay-paint focus-ring disabled:cursor-not-allowed",
                  selectedId === row.id && "border-pb-teal bg-pb-teal/5",
                )}
              >
                <p className="break-words text-sm font-semibold text-pb-text">
                  {row.item || "Untitled control"}
                </p>
                <p className="mt-1 break-words text-xs text-pb-text-muted">
                  {row.responsible || "Unassigned"} ·{" "}
                  {row.cadence || "Cadence not set"}
                </p>
              </button>
            ) : (
              <article className="grid gap-3 py-3 @lg:grid-cols-2">
                <div>
                  <p className="break-words text-sm font-semibold text-pb-text">
                    {row.item || "Untitled control"}
                  </p>
                  <p className="mt-1 break-words text-xs text-pb-text-muted">
                    {row.responsible || "Unassigned"} ·{" "}
                    {row.cadence || "Cadence not set"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-pb-text-muted">Act when</p>
                  <p className="mt-1 break-words text-sm text-pb-text">
                    {row.trigger || "No action trigger set"}
                  </p>
                </div>
              </article>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
